import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import type { IntakeData } from "@/lib/types";
import { intakeStringPreserveWhitespaceWhileTyping } from "@/lib/intakeStringSanitize";
import {
  getNationalForDisplay,
  parsePrefilledPhone,
  repairIntakePhoneFields,
  validateNationalPhone,
} from "@/lib/phoneNumber";
import {
  coerceToStringArray,
  emailRegex,
  validateCard,
} from "./cardValidation";
import { useFormResumeBootstrap } from "./useFormResumeBootstrap";
import type { ProcedureType } from "@/lib/specialtyTags";
import type { CardDef } from "./types";
import {
  patientIntakeApi,
  buildFormPayload,
  collectServerMediaIdsForProcedureReset,
  deleteMediaIdsWithLogging,
} from "@/lib/patientIntakeApi";
import { trackMetaCustomEvent, trackMetaStandardEvent } from "@/lib/metaPixel";
import {
  captureReferralCode,
  trackReferralStarted,
  trackReferralCompleted,
} from "@/lib/referralTracker";
import { useSpecialties } from "@/hooks/useSpecialties";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { i18n as linguiCatalog } from "@/i18n";
import { QUESTION_TREE } from "@/components/intake/dentist/questionTree";

import { findFirstIncompleteCardIndex } from "@/lib/hydrateIntakeData";
import { buildCardFlow, DENTISTRY_PROCEDURE_TYPE_ORDER } from "./buildCardFlow";
import { useCardFlow } from "./useCardFlow";
import { CardShell } from "./CardShell";
import { NameCard } from "./cards/NameCard";
import { TextCard } from "./cards/TextCard";
import { ContactInfoCard } from "./cards/ContactInfoCard";
import { ContactConsents } from "@/components/intake/ContactConsents";
import { LanguageCard } from "./cards/LanguageCard";
import { SpecialtyCard } from "./cards/SpecialtyCard";
import { SingleChoiceCard } from "./cards/SingleChoiceCard";
import { MultiChoiceCard } from "./cards/MultiChoiceCard";
import { HairAreaCard } from "./cards/HairAreaCard";
import { FileUploadCard } from "./cards/FileUploadCard";
import { UrlLinksCard } from "./cards/UrlLinksCard";
import { DocumentsCombinedCard } from "./cards/DocumentsCombinedCard";
import { ReviewCard } from "./cards/ReviewCard";
import { VerificationCard } from "./cards/VerificationCard";
import { SuccessCard } from "./cards/SuccessCard";
import { SubmitCelebrationCard } from "./cards/SubmitCelebrationCard";
import { OtherSpecialtyReviewCard } from "./cards/OtherSpecialtyReviewCard";
import { OtherSpecialtyThankYouCard } from "./cards/OtherSpecialtyThankYouCard";
import { DentalPhotosCard } from "./cards/DentalPhotosCard";
import { ErrorCallout } from "@/components/ui/error-callout";
import { SubmittedFormBarrier } from "./SubmittedFormBarrier";
import { Loader2 } from "lucide-react";
import { finalizeWithConsents, stripConsents } from "@/lib/buildConsents";

import "./animations.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const ENABLE_INTAKE_CACHE = true;
const INTAKE_CACHE_KEY = "intake-v2-cache";

const normalizeEmail = (e?: string) => (e ?? "").trim().toLowerCase();
/** Matches `useCardFlow` mobile history behavior (<768px). */
const isDesktopViewport = () =>
  typeof window !== "undefined" && window.innerWidth >= 768;
const getProcedureSlug = (p: IntakeData["procedures"]): string =>
  typeof p === "string" ? p : ((p as ProcedureType)?.slug ?? "");

const getFlowType = (procedureSlug: string) =>
  procedureSlug === "other" ? "other-specialty" : "standard-intake";

const getCurrentStepName = () =>
  typeof window === "undefined"
    ? ""
    : window.location.hash.replace(/^#/, "").trim();

/** Order-insensitive set equality for string arrays. */
function stringArraySetsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bs = new Set(b);
  return a.every((x) => bs.has(x));
}

const BASE_INTAKE_DATA: IntakeData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  country: "US",
  preferredLanguage: "",
  procedures: "",
  dateOfBirth: "",
  preExistingConditions: "",
  currentMedications: "",
  allergies: "",
  majorSurgeries: "",
  smokingHabits: "",
  dentalProcedureType: [],
  dentalDiscomfort: "",
  uploads: {},
  documentReferenceLinks: [],
  consentGiven: false,
  emailVerified: false,
};

const toUploadedMediaOnly = (
  files?: IntakeData["uploads"][keyof IntakeData["uploads"]],
) =>
  (files ?? []).filter(
    (f): f is Exclude<typeof f, File> =>
      typeof File !== "undefined" ? !(f instanceof File) : true,
  );

const isHttpUrl = (value: unknown): value is string =>
  typeof value === "string" &&
  (value.startsWith("https://") || value.startsWith("http://"));

const sanitizePhotoCache = (photos?: IntakeData["photos"]): IntakeData["photos"] => {
  if (!photos) return {};
  const sanitized: NonNullable<IntakeData["photos"]> = {};
  for (const [position, url] of Object.entries(photos)) {
    if (isHttpUrl(url)) {
      sanitized[position as keyof NonNullable<IntakeData["photos"]>] = url;
    }
  }
  return sanitized;
};

// eslint-disable-next-line react-refresh/only-export-components -- exported for unit tests
export const buildCachePayload = (data: IntakeData): IntakeData => ({
  ...stripConsents(data),
  formUid: undefined,
  referenceUid: undefined,
  emailVerified: false,
  consentGiven: false,
  photos: sanitizePhotoCache(data.photos),
  uploads: {
    idPassport: toUploadedMediaOnly(data.uploads?.idPassport),
    labsImaging: toUploadedMediaOnly(data.uploads?.labsImaging),
    photos: toUploadedMediaOnly(data.uploads?.photos),
  },
});

const writeCachedIntakeData = (data: IntakeData) => {
  if (!ENABLE_INTAKE_CACHE || typeof window === "undefined") return;
  try {
    const cachePayload = buildCachePayload(data);
    window.localStorage.setItem(INTAKE_CACHE_KEY, JSON.stringify(cachePayload));
  } catch {
    // Ignore storage errors; form continues without cache.
  }
};

const clearCachedIntakeData = () => {
  if (!ENABLE_INTAKE_CACHE || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(INTAKE_CACHE_KEY);
  } catch {
    // Ignore storage errors.
  }
};

const readCachedIntakeData = (): IntakeData => {
  if (!ENABLE_INTAKE_CACHE || typeof window === "undefined") {
    return BASE_INTAKE_DATA;
  }
  try {
    const raw = window.localStorage.getItem(INTAKE_CACHE_KEY);
    if (!raw) return BASE_INTAKE_DATA;
    const parsed = stripConsents(JSON.parse(raw) as Partial<IntakeData>);
    const { formUid, referenceUid, emailVerified, consentGiven, ...cacheSafe } =
      parsed;
    void formUid;
    void referenceUid;
    void emailVerified;
    void consentGiven;
    const merged = {
      ...BASE_INTAKE_DATA,
      ...cacheSafe,
      uploads: {
        ...BASE_INTAKE_DATA.uploads,
        ...(cacheSafe.uploads ?? {}),
      },
      documentReferenceLinks:
        cacheSafe.documentReferenceLinks ??
        BASE_INTAKE_DATA.documentReferenceLinks,
    };
    const repairedPhone = repairIntakePhoneFields(merged.phone, merged.country);
    return {
      ...merged,
      phone: repairedPhone.phone,
      country: repairedPhone.country,
    };
  } catch {
    return BASE_INTAKE_DATA;
  }
};

const PROCEDURE_DETAIL_STRING_KEYS: string[] = [
  "dentalDiscomfort",
  "travelConcerns",
  "travelAlone",
  "budgetExpectations",
  "personalInterest",
  "vascularSymptoms",
  "vascularDiagnosisStatus",
  "vascularUrgency",
  "vascularProceduralExpectation",
  "hairTransplantProgression",
  "hairTransplantOutcomeExpectation",
  "hairTransplantTimeline",
  "hairTransplantTreatmentAreasOther",
  "opticalVisionCorrection",
  "opticalLaterality",
  "opticalProcedureInterest",
  "plasticProcedureType",
  "plasticSpecificConcern",
  "plasticPreviousSurgery",
  "plasticOutcomePriority",
  "diagnosticsTimeSensitivity",
  "diagnosticsCategory",
  "diagnosticsReason",
  "diagnosticsReasonOther",
  "diagnosticsHasSymptoms",
  "diagnosticsSymptomOther",
  "diagnosticsConditionOther",
  "diagnosticsDoctorStatus",
  "diagnosticsDoctorList",
  "diagnosticsScope",
  "diagnosticsRecentTesting",
  "diagnosticsShareRecentResults",
  "diagnosticsLabUrgency",
  "diagnosticsFasting",
  "diagnosticsResultsUsage",
  "diagnosticsFollowUp",
  "fertilityPatientType",
  "fertilityPreviousAttempts",
  "fertilityKnownDiagnosis",
  "fertilityTimeline",
  "stemCellReasonOther",
  "stemCellDiagnosisStatus",
  "stemCellTreatmentGoal",
  "orthopedicDuration",
  "entDuration",
  "entPreviousSurgeries",
  "neuroSurgeryDiagnosisStatus",
  "neuroSurgeryPriorTreatment",
  "neuroSurgeryTimeline",
  "urologyDuration",
  "urologyDiagnosisStatus",
  "urologyPriorTreatment",
  "urologyTimeline",
  "bariatricPrimaryGoalOther",
  "bariatricProcedureInterest",
  "bariatricPreviousAttemptsOther",
  "bariatricCurrentDiet",
  "bariatricCurrentDietOther",
  "bariatricTimeline",
  "bariatricTimelineOther",
  "wellnessPrimaryGoalOther",
  "wellnessTimeline",
  "wellnessTimelineOther",
  "wellnessPreviousExperience",
  "wellnessSpecificConcernsOther",
  "wellnessAcupunctureConcern",
  "wellnessAcupunctureConcernOther",
  "wellnessAcupunctureTargetArea",
  "wellnessAcupunctureFrequency",
  "wellnessHyperbaricReason",
  "wellnessHyperbaricReasonOther",
  "wellnessHyperbaricUrgency",
  "wellnessHyperbaricSessions",
  "wellnessMassageFocus",
  "wellnessMassageFocusOther",
  "wellnessMassagePressure",
  "wellnessReflexologyTarget",
  "wellnessReflexologyMainSymptomsOther",
  "wellnessOzoneReason",
  "wellnessOzoneReasonOther",
  "wellnessOzonePreviousExperience",
  "wellnessFunctionalDoctorProgram",
  "wellnessFunctionalDoctorProgramOther",
  "wellnessFunctionalDoctorExperience",
  "wellnessAdditionalNotes",
  "otherSpecialtyTimeline",
  "otherSpecialtyAdditionalInfo",
];

const PROCEDURE_DETAIL_ARRAY_KEYS: string[] = [
  "dentalProcedureType",
  "opticalPrimaryReason",
  "plasticAreaOfInterest",
  "diagnosticsType",
  "diagnosticsArea",
  "stemCellLocation",
  "stemCellReason",
  "wellnessSpecificConcerns",
  "orthopedicAffectedArea",
  "orthopedicPrimaryConcern",
  "entPrimaryConcern",
  "neuroSurgeryPrimaryConcern",
  "urologyPrimaryConcern",
  "urologySymptoms",
  "vascularPrimaryGoal",
  "vascularSymptomTypes",
  "hairTransplantTreatmentAreas",
  "hairTransplantRelevantFactors",
  "hairTransplantPreviousTreatments",
  "opticalSymptoms",
  "stemCellPriorTreatments",
  "orthopedicPriorTreatments",
  "entSymptoms",
  "neuroSurgerySymptoms",
  "bariatricPriorWeightLoss",
  "bariatricPreviousAttempts",
  "wellnessCurrentPractices",
  "diagnosticsSymptoms",
  "diagnosticsConditions",
  "wellnessReflexologyMainSymptoms",
  "otherSpecialtyBodyAreas",
  "otherSpecialtySymptoms",
  "otherSpecialtyFocus",
  "fertilityCareFocus",
  "diagnosticsPurpose",
  "bariatricPrimaryGoal",
  "wellnessPrimaryGoal",
];

const DENTAL_DYNAMIC_FIELDS = Object.values(QUESTION_TREE).flatMap((tree) =>
  (tree.questions ?? []).map((q) => ({
    id: q.id,
    isArray: q.type === "multiselect",
  })),
);

const buildProcedureResetPatch = (prev: IntakeData): Partial<IntakeData> => {
  const patch: Record<string, unknown> = {
    uploads: {},
    documentReferenceLinks: [],
    photos: {},
    photoMediaMap: {},
    additionalPhotos: [],
    additionalPhotoMediaIds: [],
  };

  for (const key of PROCEDURE_DETAIL_STRING_KEYS) {
    if (key in (prev as unknown as Record<string, unknown>)) patch[key] = "";
  }
  for (const key of PROCEDURE_DETAIL_ARRAY_KEYS) {
    if (key in (prev as unknown as Record<string, unknown>)) patch[key] = [];
  }
  for (const q of DENTAL_DYNAMIC_FIELDS) {
    if (q.id in (prev as unknown as Record<string, unknown>)) {
      patch[q.id] = q.isArray ? [] : "";
    }
  }

  return patch as Partial<IntakeData>;
};

// eslint-disable-next-line react-refresh/only-export-components -- exported for unit tests
export const sanitize = (updates: Partial<IntakeData>): Partial<IntakeData> => {
  const out: Partial<IntakeData> = {};
  (Object.entries(updates) as [keyof IntakeData, IntakeData[keyof IntakeData]][]).forEach(
    ([k, v]) => {
      if (typeof v === "string") {
        if (k === "email") {
          (out as Record<string, unknown>)[k] = v.trim().toLowerCase();
          return;
        }
        if (intakeStringPreserveWhitespaceWhileTyping(k)) {
          (out as Record<string, unknown>)[k] = v;
          return;
        }
        (out as Record<string, unknown>)[k] = v.trim();
        return;
      }
      if (v !== undefined) (out as Record<string, unknown>)[k] = v;
    },
  );
  return out;
};

// ─── URL prefill helper ─────────────────────────────────────────────────────
/**
 * Reads contact-info fields from the URL search params and returns them
 * as a partial IntakeData object. Used so referral links can pre-fill
 * the patient's name, email, and phone automatically.
 */
function parseUrlPrefill(params: URLSearchParams): Partial<IntakeData> {
  const prefilled: Partial<IntakeData> = {};
  const get = (key: string) => {
    const v = params.get(key);
    return v ? decodeURIComponent(v).trim() : undefined;
  };
  if (get("firstName")) prefilled.firstName = get("firstName")!;
  if (get("lastName"))  prefilled.lastName  = get("lastName")!;
  if (get("email"))     prefilled.email     = get("email")!.toLowerCase();
  if (get("phone")) {
    const parsed = parsePrefilledPhone(get("phone")!, "US");
    if (parsed) {
      prefilled.country = parsed.country;
      prefilled.phone = parsed.national;
    } else {
      prefilled.phone = get("phone")!;
    }
  }
  return prefilled;
}

// ─── Main page component ──────────────────────────────────────────────────────
export interface IntakeV2PageProps {
  /**
   * Answers already collected elsewhere (voice assistant continuation).
   * When set, the local draft cache is ignored so another patient's cached
   * draft can never bleed into a resumed form.
   */
  initialData?: Partial<IntakeData>;
  /** Open at the first card the patient still has to fill in. */
  resumeAtFirstIncomplete?: boolean;
}

export default function IntakeV2Page({
  initialData,
  resumeAtFirstIncomplete = false,
}: IntakeV2PageProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL prefill (runs once on mount) ───────────────────────────────────
  // Parse URL params once at mount time; stored in a ref to be used in the
  // useState initializer below. URL params take priority over any cached data.
  const initialUrlPrefill = useMemo(
    () => parseUrlPrefill(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    captureReferralCode();
    void trackReferralStarted();

    // Remove PII query params from the URL immediately after capturing them
    // so they don't stay in browser history or get accidentally shared.
    const params = new URLSearchParams(window.location.search);
    const paramsToRemove = ["firstName", "lastName", "email", "phone"];
    const hasAny = paramsToRemove.some((k) => params.has(k));
    if (hasAny) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          paramsToRemove.forEach((k) => next.delete(k));
          return next;
        },
        { replace: true },
      );
    }
  }, []);

  // ── Trusted session ─────────────────────────────────────────────────────
  const [trustedStatus, setTrustedStatus] = useState<
    "idle" | "loading" | "ready" | "error"
  >(() => {
    if (typeof window === "undefined") return "idle";
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("formUid") && p.get("token") ? "loading" : "idle";
    } catch {
      return "idle";
    }
  });
  const trustedBootRef = useRef(false);

  // ── Intake state ────────────────────────────────────────────────────────
  const [intakeData, setIntakeData] = useState<IntakeData>(() => {
    // URL params take priority over cache so referral links always pre-fill.
    // Hydrated continuation data outranks both — and suppresses the cache.
    const cached = initialData ? BASE_INTAKE_DATA : readCachedIntakeData();
    const merged = { ...cached, ...initialUrlPrefill, ...(initialData ?? {}) };
    const repairedPhone = repairIntakePhoneFields(merged.phone, merged.country);
    return {
      ...merged,
      phone: repairedPhone.phone,
      country: repairedPhone.country,
    };
  });
  const intakeRef = useRef<IntakeData>(intakeData);
  // Continuation data is server-owned; never leave it in this browser's draft cache.
  const cacheWriteEnabledRef = useRef(ENABLE_INTAKE_CACHE && !initialData);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [trustedSessionError, setTrustedSessionError] = useState<string | null>(
    null,
  );
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});

  // ── Submit (draft / finalize) state ────────────────────────────────────
  const [isPreparingDraft, setIsPreparingDraft] = useState(false);
  const draftEmailRef = useRef<string | null>(null);
  const lastSyncSigRef = useRef<string | null>(null);
  const syncTimerRef = useRef<number | null>(null);
  const syncPromiseRef = useRef<Promise<void> | null>(null);
  const syncGenRef = useRef(0);
  /** True after review submit advanced into `submit-celebration` (mobile back stack skips that card). */
  const passedSubmitCelebrationRef = useRef(false);
  const trackedMilestonesRef = useRef<Set<string>>(new Set());

  // ── Specialties from API ────────────────────────────────────────────────
  const { specialties, isLoading: specialtiesLoading } = useSpecialties(
    intakeData.email || undefined,
    true,
  );

  // ── Card flow ───────────────────────────────────────────────────────────
  // Build dental dynamic cards using i18n so options are correctly translated
  const { i18n } = useLingui();
  const dentalDynamicCards = useMemo((): CardDef[] => {
    const selected = coerceToStringArray(intakeData.dentalProcedureType);
    if (selected.length === 0) return [];

    const orderSet = new Set<string>(
      DENTISTRY_PROCEDURE_TYPE_ORDER as unknown as string[],
    );
    const ordered = DENTISTRY_PROCEDURE_TYPE_ORDER.filter((t) =>
      selected.includes(t),
    );
    const extras = selected.filter((t) => !orderSet.has(t));
    const typesToExpand = [...ordered, ...extras];

    const cards: CardDef[] = [];
    for (const procedureType of typesToExpand) {
      const qTree = QUESTION_TREE[procedureType as keyof typeof QUESTION_TREE];
      if (!qTree?.questions) continue;
      for (const q of qTree.questions) {
        let cardType: CardDef["cardType"] = "text-input";
        let options: CardDef["options"] = undefined;
        let autoAdvance = false;
        if (q.type === "select") {
          cardType = "single-choice";
          options = q.options.map((o) => ({
            // Persist human-readable values to avoid leaking Lingui hash ids into API payloads.
            value: typeof o === "string" ? o : i18n._(o),
            label: typeof o === "string" ? o : i18n._(o),
          }));
          autoAdvance = true;
        } else if (q.type === "multiselect") {
          cardType = "multi-choice";
          options = q.options.map((o) => ({
            // Persist human-readable values to avoid leaking Lingui hash ids into API payloads.
            value: typeof o === "string" ? o : i18n._(o),
            label: typeof o === "string" ? o : i18n._(o),
          }));
        }
        const questionText: string = (() => {
          if (typeof q.label === "string") return q.label;
          if (q.label && typeof (q.label as React.ReactElement<{ id?: string }>).props?.id === "string") {
            return i18n._((q.label as React.ReactElement<{ id: string }>).props.id) || i18n._(msg`Question`);
          }
          return i18n._(msg`Question`);
        })();
        cards.push({
          id: q.id,
          cardType,
          question: questionText,
          fieldKey: q.id as keyof IntakeData,
          options,
          autoAdvance,
          placeholder:
            q.type === "textarea" ? i18n._(msg`Type your answer...`) : undefined,
        } satisfies CardDef);
      }
    }
    return cards;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeData.dentalProcedureType, i18n.locale]);

  const cards = useMemo(
    () => buildCardFlow(intakeData, trustedStatus, dentalDynamicCards, i18n),
    // rebuild when procedure, dental subtype, dynamic cards, trusted status, or locale changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      (typeof intakeData.procedures === "string"
        ? intakeData.procedures
        : (intakeData.procedures as ProcedureType)?.slug) ?? "",
      intakeData.dentalProcedureType,
      dentalDynamicCards,
      trustedStatus,
      i18n.locale,
    ],
  );

  // Resume position is computed once, on the first render that has cards.
  const resumeIndexRef = useRef<number | null>(null);
  if (resumeIndexRef.current === null && cards.length > 0) {
    resumeIndexRef.current = resumeAtFirstIncomplete
      ? findFirstIncompleteCardIndex(cards, intakeData)
      : 0;
  }

  const {
    currentIndex,
    currentCard,
    animDir,
    progressPercent,
    goNext,
    goBack,
    skipCard,
    jumpToCard,
  } = useCardFlow({
    cards,
    initialIndex: resumeIndexRef.current ?? 0,
    onCardChange: (index) => {
      const card = cards[index];
      if (!card) return;
      if (card.id !== "verification" && card.id !== "other-specialty-thank-you") return;
      const dedupeKey = `intake-confirmation:${card.id}:${intakeRef.current.formUid ?? "no-form-uid"}`;
      if (trackedMilestonesRef.current.has(dedupeKey)) return;
      trackedMilestonesRef.current.add(dedupeKey);

      trackMetaCustomEvent("IntakeConfirmationReached", {
        card_id: card.id,
        flow_type: getFlowType(getProcedureSlug(intakeRef.current.procedures)),
        has_form_uid: Boolean(intakeRef.current.formUid),
        step_name: getCurrentStepName(),
      });
    },
  });

  /** From verify-email: land on review, not the auto-advanced celebration card. */
  const handleVerificationBack = useCallback(() => {
    const usedCelebration = passedSubmitCelebrationRef.current;
    passedSubmitCelebrationRef.current = false;

    if (typeof window !== "undefined" && !isDesktopViewport() && usedCelebration) {
      window.history.go(-2);
      return;
    }
    jumpToCard("review");
  }, [jumpToCard]);

  // keep intakeRef current
  useEffect(() => {
    intakeRef.current = intakeData;
  }, [intakeData]);

  const persistToCache = useCallback((data: IntakeData) => {
    if (!cacheWriteEnabledRef.current) return;
    writeCachedIntakeData(data);
  }, []);

  const clearAndDisableCache = useCallback(() => {
    cacheWriteEnabledRef.current = false;
    clearCachedIntakeData();
  }, []);

  // Persist form values only; card/page index always starts from first card on refresh.
  useEffect(() => {
    persistToCache(intakeData);
  }, [intakeData, persistToCache]);

  // Best-effort flush right before hard refresh/tab close.
  useEffect(() => {
    const handleBeforeUnload = () => {
      persistToCache(intakeRef.current);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [persistToCache]);

  // Backfill previously stored Lingui hash ids in dentistry dynamic answers.
  useEffect(() => {
    const selected = coerceToStringArray(intakeData.dentalProcedureType);
    if (selected.length === 0) return;

    const idToLabelByQuestion = new Map<string, Record<string, string>>();
    for (const procedureType of selected) {
      const qTree = QUESTION_TREE[procedureType as keyof typeof QUESTION_TREE];
      if (!qTree?.questions) continue;
      for (const q of qTree.questions) {
        if (q.type !== "select" && q.type !== "multiselect") continue;
        const optionMap: Record<string, string> = {};
        for (const option of q.options ?? []) {
          if (typeof option === "string") continue;
          const optionId = option.id;
          if (!optionId) continue;
          optionMap[optionId] = i18n._(option);
        }
        if (Object.keys(optionMap).length > 0) {
          idToLabelByQuestion.set(q.id, optionMap);
        }
      }
    }

    if (idToLabelByQuestion.size === 0) return;

    const patch: Partial<IntakeData> = {};
    for (const [questionId, optionMap] of idToLabelByQuestion.entries()) {
      const raw = (intakeData as unknown as Record<string, unknown>)[questionId];
      if (typeof raw === "string") {
        const mapped = optionMap[raw];
        if (mapped && mapped !== raw) {
          (patch as Record<string, unknown>)[questionId] = mapped;
        }
        continue;
      }
      if (Array.isArray(raw)) {
        const mappedArray = raw.map((value) => {
          if (typeof value !== "string") return value;
          return optionMap[value] ?? value;
        });
        if (JSON.stringify(mappedArray) !== JSON.stringify(raw)) {
          (patch as Record<string, unknown>)[questionId] = mappedArray;
        }
      }
    }

    if (Object.keys(patch).length === 0) return;

    const next: IntakeData = {
      ...intakeRef.current,
      ...patch,
    };
    intakeRef.current = next;
    setIntakeData(next);
    persistToCache(next);
  }, [
    intakeData,
    intakeData.dentalProcedureType,
    i18n,
    i18n.locale,
    persistToCache,
  ]);

  // Align draft email with server form (trusted session / resume) so ensureDraft does not recreate the form
  useEffect(() => {
    if (!intakeData.formUid) {
      draftEmailRef.current = null;
      return;
    }
    if (!draftEmailRef.current) {
      const normalizedEmail = normalizeEmail(intakeData.email);
      if (normalizedEmail) {
        draftEmailRef.current = normalizedEmail;
      }
    }
  }, [intakeData.formUid, intakeData.email]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const updateIntake = useCallback((updates: Partial<IntakeData>) => {
    const clean = sanitize(updates);
    const prev = intakeRef.current;
    const oldSlug = getProcedureSlug(prev.procedures);
    const newSlug = clean.procedures
      ? getProcedureSlug(clean.procedures as IntakeData["procedures"])
      : oldSlug;
    const procedureChanged = !!clean.procedures && oldSlug !== newSlug;

    const resetPatch = procedureChanged ? buildProcedureResetPatch(prev) : {};

    // Other specialty is handled by email only — drop any draft form so we don't sync to the API.
    const switchingToOther = procedureChanged && newSlug === "other";
    if (switchingToOther) {
      lastSyncSigRef.current = null;
      draftEmailRef.current = null;
    }

    // Remove server-backed uploads (documents + dental) when specialty changes.
    if (procedureChanged) {
      const mediaIds = collectServerMediaIdsForProcedureReset(prev);
      if (mediaIds.length > 0) {
        void deleteMediaIdsWithLogging(mediaIds, {
          flow: "intake-v2",
          reason: "specialty-change",
          formUid: prev.formUid,
          oldSlug,
          newSlug,
        });
      }
    }

    const next: IntakeData = {
      ...prev,
      ...resetPatch,
      ...clean,
      ...(switchingToOther
        ? { formUid: undefined, referenceUid: undefined }
        : {}),
    };
    intakeRef.current = next;
    setIntakeData(next);
    persistToCache(next);
  }, [persistToCache]);

  const emitMilestoneOnce = useCallback(
    (
      key: string,
      emit: () => void,
    ) => {
      if (trackedMilestonesRef.current.has(key)) return;
      trackedMilestonesRef.current.add(key);
      emit();
    },
    [],
  );

  // ── Draft sync ─────────────────────────────────────────────────────────
  const performSync = useCallback(async () => {
    const gen = syncGenRef.current;
    const data = intakeRef.current;
    if (!data.formUid) return;
    if (syncPromiseRef.current) {
      try { await syncPromiseRef.current; } catch { /* already logged */ }
    }
    const latest = intakeRef.current;
    if (gen !== syncGenRef.current || !latest.formUid) return;
    if (getProcedureSlug(latest.procedures) === "other") return;
    const payload = buildFormPayload(latest);
    const sig = JSON.stringify(payload);
    if (lastSyncSigRef.current === sig) return;
    const p = (async () => {
      try {
        await patientIntakeApi.updateIntake(latest.formUid!, latest);
        if (gen === syncGenRef.current) lastSyncSigRef.current = sig;
      } catch (e) {
        console.error("Draft sync failed", e);
      }
    })();
    syncPromiseRef.current = p;
    try { await p; } finally {
      if (syncPromiseRef.current === p) syncPromiseRef.current = null;
    }
  }, []);

  const scheduleSync = useCallback(() => {
    if (!intakeRef.current.formUid) return;
    if (getProcedureSlug(intakeRef.current.procedures) === "other") return;
    if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(() => {
      syncTimerRef.current = null;
      void performSync();
    }, 800);
  }, [performSync]);

  const flushSync = useCallback(async () => {
    if (syncTimerRef.current) {
      window.clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    await performSync();
    if (syncPromiseRef.current) {
      try { await syncPromiseRef.current; } catch { /* already logged */ }
    }
  }, [performSync]);

  // cleanup on unmount
  useEffect(() => () => {
    if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    void flushSync();
  }, [flushSync]);

  // ── Trusted session bootstrap ──────────────────────────────────────────
  const trustedToken = searchParams.get("token");
  const trustedSource = searchParams.get("source") ?? undefined;
  const trustedUrlFormUid = searchParams.get("formUid");

  useEffect(() => {
    const urlFormUid = searchParams.get("formUid") || undefined;
    if (!urlFormUid || !trustedToken || trustedBootRef.current) return;
    trustedBootRef.current = true;

    // No `cancelled` flag: the token is single-use and trustedBootRef already
    // prevents a second run, so a searchParams change mid-flight must not drop
    // the result (that would leave the page stuck on "Securing your session").
    (async () => {
      setTrustedSessionError(null);
      setTrustedStatus("loading");
      try {
        const resp = await patientIntakeApi.startTrustedSession({
          formUid: urlFormUid,
          token: trustedToken,
          ...(trustedSource ? { source: trustedSource } : {}),
        });
        const data = resp?.data ?? resp;

        if (data?.accessToken) {
          try {
            localStorage.setItem("accessToken", data.accessToken);
            if (data.refreshToken) {
              localStorage.setItem("refreshToken", data.refreshToken);
            }
          } catch {
            // Ignore storage errors (e.g. private mode)
          }
        }

        const intakeForm = (data?.intakeForm || {}) as {
          formUid?: string;
          referenceUid?: string;
          identity?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
            country?: string;
            preferredLanguage?: string;
          };
        };

        const identityUpdates: Partial<IntakeData> = {};

        if (intakeForm.formUid) {
          identityUpdates.formUid = intakeForm.formUid;
        } else if (urlFormUid) {
          identityUpdates.formUid = urlFormUid;
        }

        if (intakeForm.referenceUid) {
          identityUpdates.referenceUid = intakeForm.referenceUid;
        }

        if (intakeForm.identity) {
          const identity = intakeForm.identity;
          if (identity.firstName) identityUpdates.firstName = identity.firstName;
          if (identity.lastName) identityUpdates.lastName = identity.lastName;
          if (identity.email) identityUpdates.email = identity.email;
          if (identity.phone) identityUpdates.phone = identity.phone;
          if (identity.country) identityUpdates.country = identity.country;
          if (identity.preferredLanguage) {
            if (
              identity.preferredLanguage === "en" ||
              identity.preferredLanguage === "es"
            ) {
              identityUpdates.preferredLanguage = identity.preferredLanguage;
            }
          }
        }

        identityUpdates.emailVerified = true;

        if (Object.keys(identityUpdates).length > 0) {
          updateIntake(identityUpdates);
        }

        setTrustedStatus("ready");

        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          params.delete("token");
          const query = params.toString();
          const newUrl = query
            ? `${window.location.pathname}?${query}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;
          const prevState = window.history.state;
          window.history.replaceState(
            prevState && typeof prevState === "object" && !Array.isArray(prevState)
              ? { ...(prevState as Record<string, unknown>) }
              : {},
            "",
            newUrl,
          );
        }
      } catch (err) {
        setTrustedStatus("error");
        const msgText =
          err instanceof Error
            ? err.message
            : linguiCatalog._(
                msg`This link has expired or has already been used.`,
              );
        setTrustedSessionError(msgText);
      }
    })();
  }, [searchParams, trustedToken, trustedSource, updateIntake]);

  // ── Verified redirect ──────────────────────────────────────────────────
  const verifiedParam = searchParams.get("verified");
  const queryFormUid = searchParams.get("formUid");
  const queryRefUid = searchParams.get("referenceUid");

  useEffect(() => {
    if (verifiedParam === "1" && queryFormUid) {
      updateIntake({
        formUid: queryFormUid,
        referenceUid: queryRefUid ?? undefined,
        emailVerified: true,
      });
      clearAndDisableCache();
      // Jump to verification (OTP step before thank-you)
      jumpToCard("verification");
      setSearchParams({}, { replace: true });
    }
  }, [
    verifiedParam,
    queryFormUid,
    queryRefUid,
    updateIntake,
    jumpToCard,
    setSearchParams,
    clearAndDisableCache,
  ]);

  // ── Form resume (?formUid= / ?formid=) — see useFormResumeBootstrap.ts ───
  const {
    resumeStatus,
    resumeError,
    submittedBarrierData,
    handleStartNewIntake,
    clearResumeSession,
    isResumeBootstrapPending,
  } = useFormResumeBootstrap({
    searchParams,
    setSearchParams,
    trustedToken,
    verifiedParam,
    queryFormUid,
    trustedStatus,
    specialties,
    initialUrlPrefill,
    baseIntakeData: BASE_INTAKE_DATA,
    cards,
    i18n,
    intakeRef,
    updateIntake,
    jumpToCard,
    clearCachedIntakeData,
    persistToCache,
    cacheWriteEnabledRef,
    draftEmailRef,
    lastSyncSigRef,
    syncGenRef,
    normalizeEmail,
  });

  // ── Ensure draft form (called right after email card) ─────────────────
  const ensureDraft = useCallback(async () => {
    const data = intakeRef.current;
    const email = normalizeEmail(data.email);
    if (!email) throw new Error(linguiCatalog._(msg`Email is required.`));

    const existing = data.formUid;
    const prevEmail = draftEmailRef.current;

    setIsPreparingDraft(true);
    try {
      if (existing) {
        if (prevEmail && prevEmail === email) return;
        if (!prevEmail) {
          draftEmailRef.current = email;
          return;
        }
        // Email changed — reset and create a new draft
        syncGenRef.current += 1;
        lastSyncSigRef.current = null;
        draftEmailRef.current = null;
        clearResumeSession();
        updateIntake({
          formUid: "",
          referenceUid: "",
          emailVerified: false,
          uploads: {},
          documentReferenceLinks: [],
        });
      }

      const payload: IntakeData = { ...intakeRef.current, email, uploads: data.uploads || {}, consentGiven: false, emailVerified: false };
      const resp = await patientIntakeApi.submitIntake(payload, {
        includeSpecialty: false,
      });
      const formUid = resp.data?.formUid;
      const referenceUid = resp.data?.referenceUid;
      if (!formUid) {
        throw new Error(linguiCatalog._(msg`Failed to create intake form.`));
      }
      updateIntake({ formUid, referenceUid });
      draftEmailRef.current = email;
      // Match what was actually POSTed so the next sync picks up specialty when ready.
      lastSyncSigRef.current = JSON.stringify(
        buildFormPayload(intakeRef.current, { includeSpecialty: false }),
      );
      emitMilestoneOnce(`patient-account-created:${formUid}`, () => {
        const procedureSlug = getProcedureSlug(intakeRef.current.procedures);
        const flowType = getFlowType(procedureSlug);
        const eventPayload = {
          flow_type: flowType,
          procedure_slug: procedureSlug || "unknown",
          has_reference_uid: Boolean(referenceUid),
          step_name: getCurrentStepName(),
        };
        trackMetaStandardEvent("CompleteRegistration", eventPayload);
        trackMetaCustomEvent("PatientAccountCreated", eventPayload);
      });
    } finally {
      setIsPreparingDraft(false);
    }
  }, [emitMilestoneOnce, updateIntake, clearResumeSession]);

  // ── Submit (finalize) ──────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    console.info("[Review Submit] handleSubmit started");
    if (!intakeRef.current.consentGiven) {
      console.warn("[Review Submit] Blocked: consent missing on intakeRef");
      setSubmitError(linguiCatalog._(msg`Please confirm consent before submitting.`));
      return;
    }
    setIsSubmitting(true);
    try {
      await flushSync();
      let data = intakeRef.current;

      // If form doesn't exist yet, create it
      if (!data.formUid) {
        if (!data.firstName?.trim() || !data.email?.trim() || !emailRegex.test(data.email)) {
          throw new Error(
            linguiCatalog._(msg`Please complete all required fields before submitting.`),
          );
        }
        const resp = await patientIntakeApi.submitIntake(data);
        const formUid = resp.data?.formUid;
        if (!formUid) {
          throw new Error(linguiCatalog._(msg`Failed to create intake form.`));
        }
        updateIntake({ formUid, referenceUid: resp.data?.referenceUid });
        draftEmailRef.current = normalizeEmail(data.email);
        data = intakeRef.current;
        emitMilestoneOnce(`patient-account-created:${formUid}`, () => {
          const procedureSlug = getProcedureSlug(intakeRef.current.procedures);
          const flowType = getFlowType(procedureSlug);
          const eventPayload = {
            flow_type: flowType,
            procedure_slug: procedureSlug || "unknown",
            has_reference_uid: Boolean(resp.data?.referenceUid),
            step_name: getCurrentStepName(),
          };
          trackMetaStandardEvent("CompleteRegistration", eventPayload);
          trackMetaCustomEvent("PatientAccountCreated", eventPayload);
        });
      }

      // Always push one final full payload before finalize so backend has
      // the latest answers even if a prior autosync was skipped/interrupted.
      const finalPayload = buildFormPayload(data);
      if (typeof window !== "undefined") {
        (
          window as Window & {
            __JEMA_LAST_REVIEW_SUBMIT_PAYLOAD__?: unknown;
          }
        ).__JEMA_LAST_REVIEW_SUBMIT_PAYLOAD__ = finalPayload;
      }
      console.info("[Review Submit] Final payload before finalize", finalPayload);
      await patientIntakeApi.updateIntake(data.formUid!, data);
      await finalizeWithConsents(patientIntakeApi.finalizeIntake, {
        ...data,
        formUid: data.formUid!,
      });

      clearAndDisableCache();
      emitMilestoneOnce(`intake-submitted:${data.formUid}`, () => {
        const procedureSlug = getProcedureSlug(intakeRef.current.procedures);
        const flowType = getFlowType(procedureSlug);
        const eventPayload = {
          flow_type: flowType,
          procedure_slug: procedureSlug || "unknown",
          has_reference_uid: Boolean(intakeRef.current.referenceUid),
          step_name: getCurrentStepName(),
        };
        trackMetaStandardEvent("Lead", eventPayload);
        trackMetaCustomEvent("IntakeSubmitted", eventPayload);
      });

      passedSubmitCelebrationRef.current = false;
      goNext(); // → verification
    } catch (err) {
      console.error("[Review Submit] Submit failed", err);
      const message =
        err instanceof Error
          ? err.message
          : linguiCatalog._(msg`There was an error submitting. Please try again.`);
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [flushSync, updateIntake, goNext, clearAndDisableCache, emitMilestoneOnce]);

  // ── Per-card "next" logic ──────────────────────────────────────────────
  const handleNext = useCallback(async () => {
    if (!currentCard) return;
    setSubmitError(null);

    // Validate
    const err = validateCard(currentCard.id, intakeRef.current, i18n);
    if (err) {
      if (typeof err === "string") {
        setCardErrors((prev) => ({ ...prev, [currentCard.id]: err }));
      } else {
        setCardErrors((prev) => ({ ...prev, ...err, [currentCard.id]: "error" }));
      }
      return;
    }
    setCardErrors((prev) => ({ ...prev, [currentCard.id]: "", firstName: "", lastName: "", country: "", phone: "", email: "" }));

    // Normalize phone to E.164 after contact info validation
    if (currentCard.id === "contactInfo") {
      const phoneResult = validateNationalPhone(
        getNationalForDisplay(
          intakeRef.current.phone ?? "",
          intakeRef.current.country,
        ),
        intakeRef.current.country,
      );
      if (phoneResult.valid && phoneResult.e164) {
        const e164 = phoneResult.e164;
        intakeRef.current = { ...intakeRef.current, phone: e164 };
        setIntakeData((prev) => ({ ...prev, phone: e164 }));
      }
    }

    // Special: after contactInfo card we ensure the draft form exists
    if (currentCard.id === "contactInfo") {
      const slug = getProcedureSlug(intakeRef.current.procedures);
      if (slug !== "other") {
        try {
          await ensureDraft();
          // User may have gone back after choosing a specialty — push it now.
          if (slug && intakeRef.current.formUid) {
            await flushSync();
          }
        } catch (e) {
          const message =
            e instanceof Error
              ? e.message
              : linguiCatalog._(msg`Could not prepare your form.`);
          setCardErrors((prev) => ({ ...prev, email: message }));
          return;
        }
      }
    }

    // Persist documents/links immediately when leaving upload steps (debounced sync is too late).
    if (intakeRef.current.formUid) {
      const flushBeforeLeave =
        currentCard.cardType === "documents-combined" ||
        currentCard.cardType === "file-upload" ||
        currentCard.cardType === "url-links";
      if (flushBeforeLeave) {
        await flushSync();
      } else {
        scheduleSync();
      }
    }

    const nextCard = cards[currentIndex + 1];
    emitMilestoneOnce("intake-started", () => {
      const procedureSlug = getProcedureSlug(intakeRef.current.procedures);
      const flowType = getFlowType(procedureSlug);
      const eventPayload = {
        flow_type: flowType,
        current_card_id: currentCard.id,
        next_card_id: nextCard?.id ?? "unknown",
        step_name: getCurrentStepName(),
      };
      trackMetaStandardEvent("InitiateCheckout", eventPayload);
      trackMetaCustomEvent("IntakeStarted", eventPayload);
    });

    goNext();
  }, [currentCard, ensureDraft, scheduleSync, flushSync, goNext, i18n, cards, currentIndex, emitMilestoneOnce]);

  /** Skip clears this step’s answer (and dental dynamic answers if skipping procedure type), then advances. */
  const handleSkipDetailedQuestion = useCallback(() => {
    if (!currentCard) return;
    setSubmitError(null);
    setCardErrors((prev) => ({ ...prev, [currentCard.id]: "" }));

    const isDentalType = currentCard.id === "dentalProcedureType";

    if (isDentalType) {
      const dentalReset: Record<string, unknown> = {};
      for (const q of DENTAL_DYNAMIC_FIELDS) {
        dentalReset[q.id] = q.isArray ? [] : "";
      }
      updateIntake({
        ...(dentalReset as Partial<IntakeData>),
        dentalProcedureType: [],
      });
    } else if (currentCard.cardType === "hair-area") {
      updateIntake({
        hairTransplantTreatmentAreas: [],
        hairTransplantTreatmentAreasOther: "",
      });
    } else if (currentCard.fieldKey) {
      const fk = currentCard.fieldKey as keyof IntakeData;
      if (currentCard.cardType === "multi-choice") {
        updateIntake({ [fk]: [] } as Partial<IntakeData>);
      } else {
        updateIntake({ [fk]: "" } as Partial<IntakeData>);
      }
    }

    if (intakeRef.current.formUid) scheduleSync();
    skipCard();
  }, [currentCard, updateIntake, scheduleSync, skipCard]);

  const detailQuestionSkip =
    currentCard &&
    (currentCard.cardType === "single-choice" ||
      currentCard.cardType === "multi-choice" ||
      currentCard.cardType === "text-input" ||
      currentCard.cardType === "hair-area")
      ? handleSkipDetailedQuestion
      : undefined;

  const handleVerificationSuccess = useCallback(() => {
    void trackReferralCompleted(intakeRef.current.firstName?.trim() || "");
    goNext();
  }, [goNext]);

  const handleOtherSpecialtySent = useCallback(() => {
    void trackReferralCompleted(intakeRef.current.firstName?.trim() || "");
    clearAndDisableCache();
    emitMilestoneOnce(`other-specialty-submitted:${normalizeEmail(intakeRef.current.email) || "no-email"}`, () => {
      const procedureSlug = getProcedureSlug(intakeRef.current.procedures);
      const eventPayload = {
        flow_type: getFlowType(procedureSlug),
        procedure_slug: procedureSlug || "other",
        step_name: getCurrentStepName(),
      };
      trackMetaStandardEvent("Lead", eventPayload);
      trackMetaCustomEvent("OtherSpecialtySubmitted", eventPayload);
    });
    goNext();
  }, [goNext, clearAndDisableCache, emitMilestoneOnce]);

  // ── Render card content ────────────────────────────────────────────────
  const renderCard = () => {
    if (!currentCard) return null;
    const cardErr = cardErrors[currentCard.id];
    const idx = currentIndex;

    switch (currentCard.cardType) {
      case "name-input":
        return (
          <NameCard
            card={currentCard}
            firstNameValue={intakeData.firstName ?? ""}
            lastNameValue={intakeData.lastName ?? ""}
            onFirstNameValue={(v) => updateIntake({ firstName: v })}
            onLastNameValue={(v) => updateIntake({ lastName: v })}
            onNext={handleNext}
            onBack={goBack}
            index={idx}
            firstNameError={cardErrors.firstName}
            lastNameError={cardErrors.lastName}
          />
        );
      case "text-input":
        return (
          <TextCard
            card={currentCard}
            value={(intakeData[currentCard.fieldKey as keyof IntakeData] as string) ?? ""}
            onValue={(v) => updateIntake({ [currentCard.fieldKey!]: v })}
            onNext={handleNext}
            onBack={goBack}
            onSkip={detailQuestionSkip}
            index={idx}
            error={cardErr}
            inputType="text"
          />
        );
      case "contact-info":
        return (
          <ContactInfoCard
            card={currentCard}
            countryValue={intakeData.country}
            phoneValue={getNationalForDisplay(
              intakeData.phone ?? "",
              intakeData.country,
            )}
            emailValue={intakeData.email}
            onCountryValue={(v) => updateIntake({ country: v })}
            onPhoneValue={(v) => updateIntake({ phone: v })}
            onPhonePaste={(country, national) =>
              updateIntake({ country, phone: national })
            }
            onEmailValue={(v) => updateIntake({ email: v })}
            onNext={handleNext}
            onBack={goBack}
            index={idx}
            countryError={cardErrors.country}
            phoneError={cardErrors.phone}
            emailError={cardErrors.email}
            loading={isPreparingDraft}
            phoneExtra={
              <ContactConsents data={intakeData} onUpdate={updateIntake} />
            }
          />
        );
      case "language-select":
        return (
          <LanguageCard
            card={currentCard}
            value={intakeData.preferredLanguage}
            onValue={(v) => updateIntake({ preferredLanguage: v })}
            onNext={handleNext}
            index={idx}
          />
        );
      case "procedure-select":
        return (
          <SpecialtyCard
            card={currentCard}
            value={intakeData.procedures}
            onValue={(v) => updateIntake({ procedures: v })}
            onNext={handleNext}
            onBack={goBack}
            index={idx}
            specialties={specialties}
            isLoading={specialtiesLoading}
            error={cardErr}
          />
        );
      case "single-choice": {
        const fk = currentCard.fieldKey as keyof IntakeData;
        return (
          <SingleChoiceCard
            card={currentCard}
            value={(intakeData[fk] as string) ?? ""}
            onValue={(v) => updateIntake({ [fk]: v })}
            onNext={handleNext}
            onBack={goBack}
            onSkip={detailQuestionSkip}
            index={idx}
            error={cardErr}
          />
        );
      }
      case "multi-choice": {
        const fk = currentCard.fieldKey as keyof IntakeData;
        const rawMulti = intakeData[fk] as unknown;
        const multiValue: string[] = Array.isArray(rawMulti)
          ? (rawMulti as string[])
          : typeof rawMulti === "string" && rawMulti.trim()
            ? [rawMulti.trim()]
            : [];
        const isDentalTypeSelector = currentCard.id === "dentalProcedureType";
        return (
          <MultiChoiceCard
            card={currentCard}
            value={multiValue}
            onValue={(v) => {
              if (isDentalTypeSelector) {
                const prev = coerceToStringArray(intakeData.dentalProcedureType);
                if (stringArraySetsEqual(prev, v)) {
                  updateIntake({ [fk]: v });
                  return;
                }
                const dentalReset: Record<string, unknown> = {};
                for (const q of DENTAL_DYNAMIC_FIELDS) {
                  dentalReset[q.id] = q.isArray ? [] : "";
                }
                updateIntake({
                  ...(dentalReset as Partial<IntakeData>),
                  [fk]: v,
                });
                return;
              }
              updateIntake({ [fk]: v });
            }}
            onNext={handleNext}
            onBack={goBack}
            onSkip={detailQuestionSkip}
            index={idx}
            error={cardErr}
          />
        );
      }
      case "hair-area":
        return (
          <HairAreaCard
            card={currentCard}
            value={intakeData.hairTransplantTreatmentAreas ?? []}
            onValue={(v) => updateIntake({ hairTransplantTreatmentAreas: v })}
            otherText={intakeData.hairTransplantTreatmentAreasOther ?? ""}
            onOtherTextChange={(v) =>
              updateIntake({ hairTransplantTreatmentAreasOther: v })
            }
            error={cardErr}
            onNext={handleNext}
            onBack={goBack}
            onSkip={detailQuestionSkip}
            index={idx}
          />
        );
      case "documents-combined":
        return (
          <DocumentsCombinedCard
            card={currentCard}
            formUid={intakeData.formUid}
            uploads={intakeData.uploads}
            onUploads={(u) => updateIntake({ uploads: u })}
            links={intakeData.documentReferenceLinks ?? []}
            onLinks={(v) => updateIntake({ documentReferenceLinks: v })}
            onNext={handleNext}
            onBack={goBack}
            onSkip={currentCard.isOptional ? skipCard : undefined}
            index={idx}
          />
        );
      case "file-upload":
        return (
          <FileUploadCard
            card={currentCard}
            uploads={intakeData.uploads}
            onUploads={(u) => updateIntake({ uploads: u })}
            onNext={handleNext}
            onBack={goBack}
            onSkip={currentCard.isOptional ? skipCard : undefined}
            index={idx}
          />
        );
      case "url-links":
        return (
          <UrlLinksCard
            card={currentCard}
            value={intakeData.documentReferenceLinks ?? []}
            onValue={(v) => updateIntake({ documentReferenceLinks: v })}
            onNext={handleNext}
            onBack={goBack}
            onSkip={currentCard.isOptional ? skipCard : undefined}
            index={idx}
          />
        );
      case "review":
        return (
          <ReviewCard
            card={currentCard}
            data={intakeData}
            onSubmit={handleSubmit}
            onEdit={jumpToCard}
            onUpdate={updateIntake}
            isSubmitting={isSubmitting}
            error={submitError}
            onBack={goBack}
          />
        );
      case "submit-celebration":
        return (
          <SubmitCelebrationCard
            data={intakeData}
            onComplete={goNext}
          />
        );
      case "other-specialty-review":
        return (
          <OtherSpecialtyReviewCard
            data={intakeData}
            onBack={goBack}
            onSent={handleOtherSpecialtySent}
          />
        );
      case "other-specialty-thank-you":
        return (
          <OtherSpecialtyThankYouCard data={intakeData} onBack={goBack} />
        );
      case "verification":
        return (
          <VerificationCard
            card={currentCard}
            data={intakeData}
            onUpdate={updateIntake}
            onSuccess={handleVerificationSuccess}
            onBack={handleVerificationBack}
            formUid={intakeData.formUid}
            referenceUid={intakeData.referenceUid}
          />
        );
      case "dental-photos":
        return (
          <DentalPhotosCard
            card={currentCard}
            data={intakeData}
            onUpdate={updateIntake}
            onNext={handleNext}
            onBack={goBack}
            index={idx}
          />
        );
      case "success":
        return <SuccessCard data={intakeData} />;
      default:
        return null;
    }
  };

  if (!currentCard) return null;

  if (resumeStatus === "submitted" && submittedBarrierData) {
    return (
      <SubmittedFormBarrier
        data={{
          firstName: submittedBarrierData.firstName,
          lastName: submittedBarrierData.lastName,
          email: submittedBarrierData.email,
          referenceUid: submittedBarrierData.referenceUid,
          formUid: submittedBarrierData.formUid,
        }}
        onStartNewIntake={handleStartNewIntake}
      />
    );
  }

  if (isResumeBootstrapPending) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-background px-4">
        <Loader2
          className="h-10 w-10 animate-spin text-[#166B93]"
          aria-hidden
        />
        <p className="text-center text-sm text-muted-foreground">
          {i18n._(msg`Loading your saved form…`)}
        </p>
      </div>
    );
  }

  const isTrustedBootstrapPending =
    Boolean(trustedToken && trustedUrlFormUid) &&
    trustedStatus !== "ready" &&
    trustedStatus !== "error";

  if (isTrustedBootstrapPending) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-background px-4">
        <Loader2
          className="h-10 w-10 animate-spin text-[#166B93]"
          aria-hidden
        />
        <p className="text-center text-sm text-muted-foreground">
          {i18n._(msg`Securing your session…`)}
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col">
      {resumeStatus === "error" && resumeError ? (
        <div className="shrink-0 px-4 pt-4 md:pt-6">
          <ErrorCallout
            message={resumeError}
            className="mx-auto max-w-lg"
          />
          <div className="mx-auto mt-4 flex max-w-lg justify-center">
            <button
              type="button"
              className="text-sm font-medium text-[#166B93] underline-offset-2 hover:underline"
              onClick={handleStartNewIntake}
            >
              {i18n._(msg`Start a new intake instead`)}
            </button>
          </div>
        </div>
      ) : null}
      {trustedStatus === "error" && trustedSessionError ? (
        <div className="shrink-0 px-4 pt-4 md:pt-6">
          <ErrorCallout
            message={trustedSessionError}
            className="mx-auto max-w-lg"
          />
        </div>
      ) : null}
      <div className="min-h-0 flex-1">
        <CardShell
          cardKey={`${currentCard.id}-${currentIndex}`}
          animDir={animDir}
          progressPercent={progressPercent}
          currentIndex={currentIndex}
          totalCards={cards.length}
          onNext={handleNext}
          onBack={goBack}
          isWide={currentCard.cardType === "dental-photos"}
          verticallyCenterContent={
            currentCard.cardType === "language-select" ||
            currentCard.cardType === "other-specialty-thank-you" ||
            currentCard.cardType === "submit-celebration" ||
            currentCard.cardType === "verification" ||
            currentCard.cardType === "success"
          }
        >
          {renderCard()}
        </CardShell>
      </div>
    </div>
  );
}
